/**
 * @copyright Copyright 2019 Epic Systems Corporation
 * @file useAsync API function
 * @author Roland Scott
 * @module react-async-hook\api\use-async
 */

import { ComponentType } from "react";
import { IAsyncFunction, TAsyncReturn } from "../internal";
import { IOptions, Executor, Ignorer, AsyncPlaceholder, IAsyncState, IAsyncPlaceholderProps } from "../types";
import { getArguments } from "../internal/use-async/get-arguments";
import { useAsyncInner } from "../internal/use-async/use-async-inner";

/**
 * A hook that returns tools to execute an asynchronous operation and track its progress.
 * @param asyncOperation The asynchronous operation to execute and keep track of
 * @returns A tuple consisting of: [**asyncState**, **execute**, **ignore**].
 *
 * **asyncState** - The raw state of the async operation
 *
 * **execute** - A function that will execute the async operation
 *
 * **ignore** - A function that can be used to ingore the results of the async operation (for cleanup purposes)
 */
export function useAsync<TAsync extends IAsyncFunction>(
	asyncOperation: TAsync,
): [Readonly<IAsyncState<TAsyncReturn<TAsync>>>, Executor<TAsync>, Ignorer];

/**
 * A hook that returns tools to execute an asynchronous operation and track its progress.
 * @param asyncOperation The asynchronous operation to execute and keep track of
 * @param options Options for this call
 * @returns A tuple consisting of: [**asyncState**, **execute**, **ignore**].
 *
 * **asyncState** - The raw state of the async operation
 *
 * **execute** - A function that will execute the async operation
 *
 * **ignore** - A function that can be used to ingore the results of the async operation (for cleanup purposes)
 */
export function useAsync<TAsync extends IAsyncFunction>(
	asyncOperation: TAsync,
	options: IOptions<TAsync>,
): [Readonly<IAsyncState<TAsyncReturn<TAsync>>>, Executor<TAsync>, Ignorer];

/**
 * A hook that returns tools to execute an asynchronous operation and track its progress.
 * @param asyncOperation The asynchronous operation to execute and keep track of
 * @param Placeholder A placeholder component. It will be bound to the state of the async operation and
 * returned so that it can be used as a UI placeholder while the async operation is in progress.
 * @returns A tuple consisting of: [**Placeholder**, **execute**, **ignore**, **asyncState**].
 *
 * **Placeholder** - The same placeholder you passed in, but now bound to the state of the async operation
 *
 * **execute** - A function that will execute the async operation
 *
 * **ignore** - A function that can be used to ingore the results of the async operation (for cleanup purposes)
 *
 * **asyncState** - The raw state of the async operation
 */
export function useAsync<TAsync extends IAsyncFunction, TPlaceholderProps extends IAsyncPlaceholderProps>(
	asyncOperation: TAsync,
	Placeholder: ComponentType<TPlaceholderProps>,
): [
	AsyncPlaceholder<TPlaceholderProps>,
	Executor<TAsync>,
	Ignorer,
	Readonly<IAsyncState<TAsyncReturn<TAsync>>>,
];

/**
 * A hook that returns tools to execute an asynchronous operation and track its progress.
 * @param asyncOperation The asynchronous operation to execute and keep track of
 * @param Placeholder A placeholder component. It will be bound to the state of the async operation and
 * returned so that it can be used as a UI placeholder while the async operation is in progress.
 * @param options Options for this call
 * @returns A tuple consisting of: [**Placeholder**, **execute**, **ignore**, **asyncState**].
 *
 * **Placeholder** - The same placeholder you passed in, but now bound to the state of the async operation
 *
 * **execute** - A function that will execute the async operation
 *
 * **ignore** - A function that can be used to ingore the results of the async operation (for cleanup purposes)
 *
 * **asyncState** - The raw state of the async operation
 */
export function useAsync<TAsync extends IAsyncFunction, TPlaceholderProps extends IAsyncPlaceholderProps>(
	asyncOperation: TAsync,
	Placeholder: ComponentType<TPlaceholderProps>,
	options: IOptions<TAsync>,
): [
	AsyncPlaceholder<TPlaceholderProps>,
	Executor<TAsync>,
	Ignorer,
	Readonly<IAsyncState<TAsyncReturn<TAsync>>>,
];

export function useAsync<TAsync extends IAsyncFunction, TPlaceholderProps extends IAsyncPlaceholderProps>(
	asyncOperation: TAsync,
	placeHolderOrOptions?: ComponentType<TPlaceholderProps> | IOptions<TAsync>,
	options?: IOptions<TAsync>,
):
	| [Readonly<IAsyncState<TAsyncReturn<TAsync>>>, Executor<TAsync>, Ignorer]
	| [
			AsyncPlaceholder<TPlaceholderProps>,
			Executor<TAsync>,
			Ignorer,
			Readonly<IAsyncState<TAsyncReturn<TAsync>>>,
			// tslint:disable-next-line: indent
	  ] {
	const {
		Placeholder,
		executeImmediately,
		initialArguments,
		displayName,
		onFailure,
		onSuccess,
	} = getArguments(placeHolderOrOptions, options);

	return useAsyncInner(
		asyncOperation,
		executeImmediately,
		Placeholder,
		initialArguments,
		onFailure,
		onSuccess,
		displayName,
	);
}
